import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"

/**
 * The finance scopes a page can be read in.
 *
 * `Global` consolidates the whole workspace — every branch AND the parish — and exists only in multi-branch
 * workspaces, where the parish is a branch-shaped object rather than a vague "all". Branch and Parish each
 * resolve to exactly one branch id; Global resolves to a set, which is why only the finance module accepts it
 * (see the note on `overviewBaseFields.context` in the API).
 */
export enum FinanceContext {
  Branch = "branch",
  Parish = "parish",
  Global = "global",
}

export const FINANCE_CONTEXTS = [FinanceContext.Branch, FinanceContext.Parish, FinanceContext.Global] as const

/**
 * The enum's VALUES as a union, so the ~110 existing `context === "parish"` comparisons keep compiling while
 * new code reaches for `FinanceContext.Parish`. CLAUDE.md bans hand-written value-set unions; this one is
 * derived from the enum rather than being a second, drifting source of truth.
 */
export type FinancePageContext = `${FinanceContext}`

/** The 7 primary Finance destinations. Accounting reports live under Reports, configuration under
 * Settings, and the audit explorer under Investigations (each a nested route group). */
export enum FinanceTab {
  Overview = "overview",
  Transactions = "transactions",
  Transfers = "transfers",
  Funds = "funds",
  Accounts = "accounts",
  Reconciliation = "reconciliation",
  Pledges = "pledges",
  Reports = "reports",
  Investigations = "investigations",
  Settings = "settings",
}

/** Reports nested-route tabs (accounting views). */
export enum FinanceReportTab {
  Statements = "statements",
  TrialBalance = "trial-balance",
  GeneralLedger = "general-ledger",
  Journal = "journal",
  ChartOfAccounts = "chart-of-accounts",
}

/** Settings nested-route tabs (finance configuration). */
export enum FinanceSettingsTab {
  /** The branch's accounting identity (IBA-119) — until it is stated, the ledger refuses to record money. */
  Setup = "setup",
  Categories = "categories",
  Accounts = "accounts",
  /** The branch's accounting currency (IBA-114) — accounting belongs to branches, not the workspace. */
  Currency = "currency",
  FiscalPeriods = "fiscal-periods",
  OpeningBalances = "opening-balances",
  Receipts = "receipts",
  // Reconciliation MOVED out of Settings to its own Finance section (IBA-161): it is recurring operational
  // work, not configuration, and it needs a nav home that can carry a review-queue badge.
}

/** @deprecated Prefer {@link FinanceTab}. Kept as an alias for existing consumers. */
export type FinanceTabKey = FinanceTab

export const BRANCH_FINANCE_TABS = [
  FinanceTab.Overview,
  FinanceTab.Transactions,
  FinanceTab.Accounts,
  FinanceTab.Transfers,
  FinanceTab.Reconciliation,
  FinanceTab.Funds,
  FinanceTab.Pledges,
  FinanceTab.Reports,
  FinanceTab.Investigations,
  FinanceTab.Settings,
] as const
export const PARISH_FINANCE_TABS = [
  FinanceTab.Overview,
  FinanceTab.Transactions,
  FinanceTab.Accounts,
  FinanceTab.Transfers,
  FinanceTab.Reconciliation,
  FinanceTab.Funds,
  FinanceTab.Reports,
  FinanceTab.Investigations,
  FinanceTab.Settings,
] as const

export const FINANCE_TABS = [...BRANCH_FINANCE_TABS, ...PARISH_FINANCE_TABS] as const

export const DEFAULT_FINANCE_TAB: FinanceTab = FinanceTab.Overview

export function parseFinanceContextFromQuery(value: string | null | undefined): FinancePageContext {
  return value === "parish" ? "parish" : "branch"
}

export function getFinanceTabsForContext(context: FinancePageContext): readonly FinanceTab[] {
  return context === "parish" ? PARISH_FINANCE_TABS : BRANCH_FINANCE_TABS
}

export function parseFinanceTabFromPathname(
  pathname: string,
  context: FinancePageContext = "branch",
): FinanceTab {
  if (pathname.includes("/finance/transactions")) return FinanceTab.Transactions
  // Same-branch account transfers are an immediate accounting transaction flow. They live under
  // /finance/transfers/create for legacy URL compatibility, but must not be treated as the multi-branch
  // Fund Transfers section because mono-branch workspaces hide that workflow entirely.
  if (pathname.includes("/finance/transfers/create")) return FinanceTab.Transactions
  if (pathname.includes("/finance/transfers")) return FinanceTab.Transfers
  if (pathname.includes("/finance/reconciliation")) return FinanceTab.Reconciliation
  if (pathname.includes("/finance/funds")) return FinanceTab.Funds
  // The Chart of Accounts destination (list + account detail). Checked before Settings so it wins its path.
  if (pathname.includes("/finance/accounts")) return FinanceTab.Accounts
  if (pathname.includes("/finance/pledges") && context === "branch") return FinanceTab.Pledges
  // Reports absorbs the legacy accounting routes; settings absorbs categories/accounts/setup;
  // investigations absorbs the legacy audit routes (for correct highlighting during redirects).
  if (pathname.includes("/finance/reports") || pathname.includes("/finance/accounting")) return FinanceTab.Reports
  if (pathname.includes("/finance/investigations") || pathname.includes("/finance/audit")) {
    return FinanceTab.Investigations
  }
  if (
    pathname.includes("/finance/settings") ||
    pathname.includes("/finance/setup") ||
    pathname.includes("/finance/categories")
  ) {
    return FinanceTab.Settings
  }
  return FinanceTab.Overview
}

export function getFinancePathForTab(tab: FinanceTab): string {
  switch (tab) {
    case FinanceTab.Transactions:
      return "/finance/transactions"
    case FinanceTab.Transfers:
      return "/finance/transfers"
    case FinanceTab.Reconciliation:
      return "/finance/reconciliation"
    case FinanceTab.Funds:
      return "/finance/funds"
    case FinanceTab.Pledges:
      return "/finance/pledges"
    case FinanceTab.Reports:
      return "/finance/reports"
    case FinanceTab.Investigations:
      return "/finance/investigations"
    case FinanceTab.Settings:
      return "/finance/settings"
    default:
      return "/finance"
  }
}

export function buildFinanceAdminUrl(input: {
  workspaceSlug?: string | null
  branchSlug?: string | null
  tab: FinanceTab
}) {
  if (!input.workspaceSlug || !input.branchSlug) {
    return "/dashboard"
  }

  return buildBranchAdminUrl(input.branchSlug, getFinancePathForTab(input.tab), input.workspaceSlug)
}

export function isFinanceTabAllowed(context: FinancePageContext, tab: FinanceTab) {
  return getFinanceTabsForContext(context).includes(tab)
}

export function getDefaultFinanceContext(input: {
  canViewBranchFinance: boolean
  canViewParishFinance: boolean
}): FinancePageContext {
  if (input.canViewParishFinance) return "parish"
  return "branch"
}
